import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { swingCollectionsService } from "../../../../../services/swingCollections";
import { useInvalidate } from "../../../../../hooks/useInvalidate";
import Input from "../../../../../components/shared/Input/Input";
import Divider from "../../../../../components/shared/Divider/Divider";
import Button from "../../../../../components/shared/Button/Button";
import { useUserData } from "../../../../../hooks/useUserData";
import { useSwingLibraryStore } from "../../../store/swingLibraryStore";
import ModalMobile from "../../../../../components/shared/Modal/ModalMobile";
import {
  COLLECTION_NAME_MAX_LENGTH,
  COLLECTION_NAME_MIN_LENGTH,
  COLLECTION_NAME_PATTERN,
} from "../../../../swingCollections/constants/collectionsJoinSymbolUrl";
import { useAllSwingCollectionsByUserId } from "../../../../swingCollections/hooks/useAllSwingCollectionsByUserId";

const normalizeName = (value = "") => value.trim().toLowerCase();

const baseSchema = z.object({
  name: z
    .string()
    .trim()
    .min(
      COLLECTION_NAME_MIN_LENGTH,
      `Minimum ${COLLECTION_NAME_MIN_LENGTH} characters`
    )
    .max(
      COLLECTION_NAME_MAX_LENGTH,
      `Maximum ${COLLECTION_NAME_MAX_LENGTH} characters`
    )
    .regex(
      COLLECTION_NAME_PATTERN,
      "Only letters, numbers, spaces and _ - ( ) ."
    ),
});

export default function CreateCollectionModal({
  isOpen,
  onClose,
  videoIds = [],
}) {
  const invalidate = useInvalidate();
  const history = useHistory();
  const { resetPagination, resetRowSelection } = useSwingLibraryStore();

  const { collections } = useAllSwingCollectionsByUserId();

  const { userData } = useUserData();

  const [loading, setLoading] = useState(false);

  const normalizedNames = useMemo(() => {
    return new Set(
      (collections || []).map((collection) =>
        normalizeName(collection.collectionName)
      )
    );
  }, [collections]);

  const schema = useMemo(() => {
    return baseSchema.refine(
      ({ name }) => !normalizedNames.has(normalizeName(name)),
      {
        message: "Collection name already exists",
        path: ["name"],
      }
    );
  }, [normalizedNames]);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { name: "" },
    mode: "onChange",
    resolver: zodResolver(schema),
  });

  const count = videoIds?.length || 0;

  const closeAndReset = () => {
    reset({ name: "" });
    onClose?.();
  };

  const onSubmit = async ({ name }) => {
    try {
      setLoading(true);
      const created = await swingCollectionsService.createSwingCollection({
        collectionName: name,
        userId: userData?.uid,
        videoIds: videoIds,
      });
      toast.success("Collection created");
      await invalidate(["getAllSwingCollectionsByUserId_key"]);

      const collectionId = created?.id;
      if (collectionId) {
        history.push(`/collections/${collectionId}`);
      }

      resetPagination();
      resetRowSelection();
      closeAndReset();
    } catch (error) {
      const msg = error?.message || "Failed to create collection";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalMobile
      className="w-full max-w-lg overflow-hidden p-0"
      isOpen={isOpen}
      onClose={closeAndReset}
    >
      <div className="mb-2 p-6">
        <Input
          control={control}
          fullWidth
          label="Collection name"
          name="name"
          placeholder="e.g., Range Session 2025-10-31"
          size="sm"
        />

        <div className="text-gray700 mt-5 text-sm font-semibold">
          {count} {count === 1 ? "swing" : "swings"}
        </div>
      </div>

      <Divider color="gray200" type="horizontal" />

      <div className="grid grid-cols-2 justify-items-start items-start gap-3 p-6">
        <Button
          color="primary500"
          isDisabled={loading}
          onClick={handleSubmit(onSubmit)}
          size="md"
        >
          {loading ? "Creating…" : "Confirm Collection"}
        </Button>

        <Button color="gray100" onClick={closeAndReset} size="md">
          Cancel
        </Button>
      </div>
    </ModalMobile>
  );
}
